import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createWorker, updateWorker } from '../../redux/features/workerSlice';
import { toast } from 'react-toastify';
import { X, Palette, User } from 'lucide-react';
import WorkerAvailabilityCalendar from './WorkerAvailabilityCalendar';

const labelClass = 'block text-xs font-medium text-gray-300 mb-1';
const inputClass =
  'block w-full h-8 rounded-md border border-gray-600 bg-gray-700/90 text-white text-xs px-2.5 py-1.5 shadow-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500/40 focus:border-blue-500 transition-colors';
const hintClass = 'mt-1 text-[11px] text-gray-500 leading-snug';

const WorkerForm = ({ isEdit, worker, onClose }) => {
  const specializationOptions = [
    { value: 'pomocni radnik', label: 'Pomoćni radnik', coefficient: 1 },
    { value: 'junior', label: 'Junior', coefficient: 2 },
    { value: 'medior', label: 'Medior', coefficient: 3 },
    { value: 'senior', label: 'Senior', coefficient: 4 },
    { value: 'leader', label: 'Leader', coefficient: 5 }
  ];

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialization: 'junior',
    specializationCoefficient: 2,
    active: true,
    color: '#3B82F6',
    maxDailyInterventions: 10,
    availabilityOverrides: []
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (isEdit && worker) {
      setFormData({
        firstName: worker.firstName || '',
        lastName: worker.lastName || '',
        email: worker.email || '',
        phone: worker.phone || '',
        specialization: worker.specialization || 'junior',
        specializationCoefficient: worker.specializationCoefficient || 2,
        active: worker.active !== undefined ? worker.active : true,
        color: worker.color || '#3B82F6',
        maxDailyInterventions: worker.maxDailyInterventions ?? 10,
        availabilityOverrides: Array.isArray(worker.availabilityOverrides)
          ? worker.availabilityOverrides
          : []
      });
    }
  }, [isEdit, worker]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'specialization') {
      const selectedOption = specializationOptions.find((opt) => opt.value === value);
      setFormData((prev) => ({
        ...prev,
        specialization: value,
        specializationCoefficient: selectedOption ? selectedOption.coefficient : 1
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        experience: isEdit ? (Number(worker?.experience) || 0) : 0,
        maxDailyInterventions: Math.min(50, Math.max(1, Number(formData.maxDailyInterventions) || 10))
      };

      if (isEdit) {
        await dispatch(updateWorker({ id: worker._id, updatedWorkerData: payload }));
        toast.success('Radnik je uspešno ažuriran');
      } else {
        await dispatch(createWorker({ workerData: payload }));
        toast.success('Radnik je uspešno dodat');
      }
      onClose();
    } catch (error) {
      toast.error(error.message || 'Došlo je do greške');
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl shadow-xl border border-gray-700/60 overflow-hidden w-full">
      <div className="flex justify-between items-center px-4 py-3 border-b border-gray-700/80">
        <h2 className="text-base font-semibold text-white">
          {isEdit ? 'Izmeni radnika' : 'Dodaj novog radnika'}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700/60 transition-colors"
          aria-label="Zatvori"
        >
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="px-4 py-4 space-y-4">
          <div className="max-w-md mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-3">
              <div>
                <label className={labelClass} htmlFor="worker-firstName">
                  Ime
                </label>
                <input
                  id="worker-firstName"
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="worker-lastName">
                  Prezime
                </label>
                <input
                  id="worker-lastName"
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="worker-email">
                  Email
                </label>
                <input
                  id="worker-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="worker-phone">
                  Telefon
                </label>
                <input
                  id="worker-phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="worker-specialization">
                  Specijalizacija
                </label>
                <select
                  id="worker-specialization"
                  name="specialization"
                  value={formData.specialization}
                  onChange={handleChange}
                  className={inputClass}
                  required
                >
                  {specializationOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} (Koeficijent: {option.coefficient})
                    </option>
                  ))}
                </select>
                {formData.specializationCoefficient && (
                  <p className={hintClass}>Koeficijent: {formData.specializationCoefficient}</p>
                )}
              </div>

              <div className="sm:max-w-[9.5rem]">
                <label className={labelClass} htmlFor="worker-maxDaily">
                  Maks. intervencija po danu
                </label>
                <input
                  id="worker-maxDaily"
                  type="number"
                  name="maxDailyInterventions"
                  value={formData.maxDailyInterventions}
                  onChange={handleChange}
                  className={inputClass}
                  min="1"
                  max="50"
                  required
                />
              </div>
            </div>
          </div>

          {isEdit && (
            <div>
              <label className={`${labelClass} mb-2`}>Kalendar dostupnosti</label>
              <WorkerAvailabilityCalendar
                overrides={formData.availabilityOverrides}
                onChange={(nextOverrides) =>
                  setFormData((prev) => ({ ...prev, availabilityOverrides: nextOverrides }))
                }
              />
            </div>
          )}

          <div className="rounded-md border border-gray-700/60 bg-gray-900/35 p-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="text-xs font-medium text-gray-300 flex items-center gap-1 shrink-0">
                <Palette className="h-3.5 w-3.5 text-gray-400" />
                Boja radnika
              </span>
              <input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className="h-8 w-10 rounded-md border border-gray-600 bg-gray-700 cursor-pointer p-0.5 shrink-0"
                aria-label="Izaberi boju"
              />
              <input
                type="text"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className={`${inputClass} w-24 shrink-0`}
                placeholder="#3B82F6"
                pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
              />
              <div
                className="h-8 w-8 rounded-md border border-gray-600 flex items-center justify-center shrink-0"
                style={{ backgroundColor: formData.color }}
              >
                <User
                  className="h-4 w-4 text-white"
                  style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }}
                />
              </div>
              <p className={`${hintClass} mb-0 shrink min-w-0`}>
                Boja u kalendaru za identifikaciju radnika
              </p>
              <label className="flex items-center gap-2 cursor-pointer shrink-0 sm:ml-auto">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  className="h-3.5 w-3.5 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-1 focus:ring-blue-500/40 focus:ring-offset-0"
                />
                <span className="text-xs text-gray-300 whitespace-nowrap">Aktivan radnik</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-700/80 bg-gray-800/80">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-gray-600 text-xs font-medium text-gray-300 bg-gray-700/80 hover:bg-gray-600 transition-colors"
          >
            Otkaži
          </button>
          <button
            type="submit"
            className="px-3 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            {isEdit ? 'Sačuvaj izmene' : 'Dodaj radnika'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WorkerForm;
